import { AiDashboard } from '@app/components/icons/ai-dashboard';
import { AiEmail } from '@app/components/icons/ai-email';
import { AiWriter } from '@app/components/icons/ai-writer';
import { Tooltip } from '@app/components/tooltip';
import { Loading } from '@app/routes/apps/components/loading';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { useCreatorLabAnalytics } from '../../../routes/lab/hooks';
import { ContentType, contentTypeToEditorTab } from '../domain';
import {
  selectContentComposerContentHistory,
  selectIsContentGenerationBlockedForType,
} from '../selector';

type ContentTypeCardProps = {
  contentType: ContentType;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonText: string;
  isDisabled?: boolean;
  tooltipText?: string;
};

export const ContentTypeCard: React.FC<ContentTypeCardProps> = ({
  contentType,
  onClick,
  icon,
  title,
  description,
  buttonText,
  isDisabled = false,
  tooltipText = '',
}) => {
  return (
    <div
      className="flex flex-col gap-3"
      data-testid={`content-type-card-${contentType}`}
    >
      <div
        className={cx(
          'flex flex-col items-center justify-between h-auto py-1 px-4 border border-spring-neutral-b0/20 rounded-[10px]',
        )}
      >
        <div className="h-11 w-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">{icon}</div>
            <p className="text-sm font-medium leading-[1.43] text-white m-0 truncate">
              {title}
            </p>
          </div>
          <Tooltip
            tooltip={tooltipText}
            shouldShow={isDisabled && !!tooltipText}
            placement="bottom-center"
          >
            <button
              type="button"
              onClick={onClick}
              disabled={isDisabled}
              className={cx(
                'h-6 min-w-14 w-auto px-3 flex items-center justify-center rounded-[4px]',
                'bg-spring-neutral-base text-white text-xs font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'focus:ring-offset-black shrink-0',
                {
                  'opacity-50 cursor-not-allowed': isDisabled,
                  'hover:bg-spring-neutral-b0/10 cursor-pointer': !isDisabled,
                },
              )}
            >
              {isDisabled && <Loading size="small" className="mr-1" />}
              {buttonText}
            </button>
          </Tooltip>
        </div>
        {description && (
          <p className="text-xs font-medium leading-[1.5] text-spring-neutral-b3 m-0 mt-1 mb-2">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

export const ContentTypeCardList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contentHistory = useAppSelector(selectContentComposerContentHistory);

  const isGeneratingSocialPost = useAppSelector(state =>
    selectIsContentGenerationBlockedForType(state, 'SOCIAL_POST'),
  );
  const isGeneratingBlogPost = useAppSelector(state =>
    selectIsContentGenerationBlockedForType(state, 'BLOG_POST'),
  );
  const isGeneratingEmail = useAppSelector(state =>
    selectIsContentGenerationBlockedForType(state, 'EMAIL'),
  );

  const isGeneratingByType = useMemo(
    () => ({
      SOCIAL_POST: isGeneratingSocialPost,
      BLOG_POST: isGeneratingBlogPost,
      EMAIL: isGeneratingEmail,
    }),
    [isGeneratingSocialPost, isGeneratingBlogPost, isGeneratingEmail],
  );

  const { trackContentTypeAction } = useCreatorLabAnalytics();

  // Use total from API response instead of history.length for accurate count
  const contentHistoryCount = useMemo(
    () => ({
      SOCIAL_POST: contentHistory['SOCIAL_POST']?.total ?? 0,
      BLOG_POST: contentHistory['BLOG_POST']?.total ?? 0,
      EMAIL: contentHistory['EMAIL']?.total ?? 0,
    }),
    [contentHistory],
  );

  /**
   * Handles Start/Open button click on content type cards.
   * - If history exists (Open): switches to the content type tab immediately
   * - If no history (Start): triggers auto-generation, tab switch happens after success in saga
   */
  const handleContentTypeStart = useCallback(
    (contentType: ContentType) => {
      const hasHistory = contentHistoryCount[contentType] > 0;

      trackContentTypeAction(contentType, hasHistory ? 'open' : 'start');

      if (hasHistory) {
        // Tab exists, switch immediately
        dispatch({
          type: '[recording] context menu tab changed',
          payload: { tabId: contentTypeToEditorTab[contentType] },
        });
      } else if (!isGeneratingByType[contentType]) {
        // Fresh start: trigger initial generation, saga will switch tab after success
        dispatch({
          type: '[content-composer] generate initial content',
          payload: { contentType },
        });
      }
    },
    [dispatch, contentHistoryCount, isGeneratingByType, trackContentTypeAction],
  );

  // Tooltip text for disabled state (only when generating and no history)
  const generatingTooltip = t(
    'content-recording.lab.content-generation-in-progress',
  );

  const contentTypeCardPropsList: ContentTypeCardProps[] = [
    {
      contentType: 'SOCIAL_POST',
      icon: <AiDashboard className="size-5" />,
      title: t(
        'content-recording.content-composer.content-types.social-media.title',
      ),
      description: !contentHistoryCount['SOCIAL_POST']
        ? t(
            'content-recording.content-composer.content-types.social-media.description',
          )
        : '',
      buttonText: contentHistoryCount['SOCIAL_POST']
        ? t('content-recording.content-composer.open')
        : t('content-recording.content-composer.start'),
      onClick: () => handleContentTypeStart('SOCIAL_POST'),
      isDisabled:
        !contentHistoryCount['SOCIAL_POST'] &&
        isGeneratingByType['SOCIAL_POST'],
      tooltipText: isGeneratingByType['SOCIAL_POST'] ? generatingTooltip : '',
    },
    {
      contentType: 'BLOG_POST',
      icon: <AiWriter className="size-5" />,
      title: t('content-recording.content-composer.content-types.blog.title'),
      description: !contentHistoryCount['BLOG_POST']
        ? t('content-recording.content-composer.content-types.blog.description')
        : '',
      buttonText: contentHistoryCount['BLOG_POST']
        ? t('content-recording.content-composer.open')
        : t('content-recording.content-composer.start'),
      onClick: () => handleContentTypeStart('BLOG_POST'),
      isDisabled:
        !contentHistoryCount['BLOG_POST'] && isGeneratingByType['BLOG_POST'],
      tooltipText: isGeneratingByType['BLOG_POST'] ? generatingTooltip : '',
    },
    {
      contentType: 'EMAIL',
      icon: <AiEmail className="size-5" />,
      title: t('content-recording.content-composer.content-types.email.title'),
      description: !contentHistoryCount['EMAIL']
        ? t(
            'content-recording.content-composer.content-types.email.description',
          )
        : '',
      buttonText: contentHistoryCount['EMAIL']
        ? t('content-recording.content-composer.open')
        : t('content-recording.content-composer.start'),
      onClick: () => handleContentTypeStart('EMAIL'),
      isDisabled: !contentHistoryCount['EMAIL'] && isGeneratingByType['EMAIL'],
      tooltipText: isGeneratingByType['EMAIL'] ? generatingTooltip : '',
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-[1.5] font-medium text-spring-neutral-b0 mb-0">
        {t('content-recording.content-composer.choose-how-to-start')}
      </p>

      {contentTypeCardPropsList.map(props => (
        <ContentTypeCard key={props.contentType} {...props} />
      ))}
    </div>
  );
};
